import { MILLISECONDS } from '@/libs/core/src/lib/constants/time';
import { EvaluationListItem } from '@chiroup/core/types/Evaluation.type';
import { use } from 'react';
import { useQuery } from 'react-query';
import { endpoints } from '../components/clinical-skills/hooks/useItemList';
import { MeContext } from '../contexts/me.context';
import { useSandboxClinicalData } from '../contexts/sandboxClinicalData.context';
import itemService from '../services/item.service';

export const queryKey = 'evaluations';

const listQuery =
  (clinicId: number, uid: string, hide: boolean, evalsThatWeNeed?: number[]) => async () => {
    return await itemService.list(
      endpoints[queryKey],
      { skip: 0, limit: 100000, hide, evalsThatWeNeed },
      clinicId,
      uid
    );
  };

type useEvaluationsProps = {
  hide?: boolean;
  refetchOnMount?: boolean;
  evalsThatWeNeed?: number[];
};

const useEvaluations = ({
  hide = true,
  refetchOnMount = true,
  evalsThatWeNeed,
}: useEvaluationsProps = {}) => {
  const sandbox = useSandboxClinicalData();
  const { me } = use(MeContext);
  const clinicId = me.selectedClinic?.ID || -1;
  // Sort/dedupe so the query key is stable regardless of caller array order —
  // the server treats evalsThatWeNeed as a set.
  const neededIds = evalsThatWeNeed?.length
    ? [...new Set(evalsThatWeNeed)].sort((a, b) => a - b)
    : undefined;
  // Key on the request parameters (NOT a per-mount id) so concurrent mounts on
  // one page dedupe into a single fetch and share the 1h staleTime.
  const { data, isFetching } = useQuery<{
    data: EvaluationListItem[];
  }>(
    [queryKey, clinicId, me.ID, hide, neededIds],
    sandbox
      ? () => ({
          data: sandbox.evaluations.filter(
            evaluation => !neededIds || neededIds.includes(evaluation.ID)
          ),
        })
      : listQuery(clinicId, me.ID, hide, neededIds),
    {
      enabled: !!sandbox || !!me?.ID,
      refetchOnWindowFocus: false,
      refetchOnMount,
      staleTime: MILLISECONDS.inHours(1),
    }
  );

  return {
    data,
    isFetching,
  };
};

export default useEvaluations;
